import { can, type RunEmail } from '@atlas-control/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Check, CircleDashed, Copy, Eye, EyeOff, RefreshCw, Send } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { DisabledReason } from '@/components/disabled-reason';
import { StatusBadge } from '@/components/status-badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { EmailStatusBadge } from '@/features/email/badges';
import { runEmailQuery, useEmailMutations } from '@/features/email/api';
import { useMe } from '@/lib/auth';
import { formatDateTime } from '@/lib/format';
import { toastError, toastSuccess } from '@/lib/notify';

export const Route = createFileRoute('/_app/runs/$runId/email')({
  component: EmailTab,
});

/** The only email a client receives: credentials and the Canvas-AI link, after the final review. */
function EmailTab() {
  const { runId } = Route.useParams();
  const q = useQuery(runEmailQuery(runId));
  if (q.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!q.data) return null;
  const e = q.data;

  return (
    <div className="space-y-6">
      {e.atlasAlsoEmails && (
        <Alert>
          <AlertDescription>
            Atlas still sends its own magic-link email to the client when a run finishes (Atlas
            change A2, pending P3). Until that is switched off, the client may receive both.
          </AlertDescription>
        </Alert>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <PolicyCard runId={runId} data={e} />
        <CredentialsCard runId={runId} data={e} />
      </div>
      <MessageCard runId={runId} data={e} />
    </div>
  );
}

type When = 'ready' | 'delay' | 'halt';

function PolicyCard({ runId, data }: { runId: string; data: RunEmail }) {
  const { user } = useMe();
  const m = useEmailMutations(runId);
  const saved: When = data.policy.halted
    ? 'halt'
    : data.policy.delayDays !== null
      ? 'delay'
      : 'ready';
  const [when, setWhen] = useState<When>(saved);
  const [days, setDays] = useState(data.policy.delayDays?.toString() ?? '');
  const allowed = can(user.role, 'email.manage') && data.message?.status !== 'sent';
  const delay = when === 'delay' && days.trim() ? Number(days) : null;
  const changed = when !== saved || (when === 'delay' && delay !== data.policy.delayDays);
  const save = (blocked: boolean) =>
    m.policy.mutate(
      { blocked, delayDays: delay, halted: when === 'halt' },
      { onSuccess: () => toastSuccess('Email policy saved'), onError: (err) => toastError(err) },
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Run email policy</CardTitle>
        <CardDescription>Applies to every email of this run.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="block-run"
            checked={data.policy.blocked}
            disabled={!allowed || m.policy.isPending}
            onCheckedChange={(c) => save(c === true)}
          />
          <Label htmlFor="block-run">Block all email for this run</Label>
        </div>
        <fieldset className="space-y-2" disabled={!allowed}>
          <legend className="mb-1 text-sm font-medium">When everything is ready</legend>
          <RadioGroup value={when} onValueChange={(v) => setWhen(v as When)}>
            <Label className="font-normal">
              <RadioGroupItem value="ready" /> Send as soon as ready
            </Label>
            <Label className="flex-wrap font-normal">
              <RadioGroupItem value="delay" /> Send
              <Input
                aria-label="Days after the final review"
                type="number"
                min={0}
                className="h-8 w-20"
                value={days}
                onChange={(ev) => {
                  setDays(ev.target.value);
                  setWhen('delay');
                }}
              />
              days after the final review
            </Label>
            <Label className="font-normal">
              <RadioGroupItem value="halt" /> Halt: wait until someone presses Send now
            </Label>
          </RadioGroup>
        </fieldset>
        <Button
          variant="outline"
          disabled={!allowed || !changed || (when === 'delay' && delay === null)}
          onClick={() => save(data.policy.blocked)}
        >
          Save
        </Button>
      </CardContent>
    </Card>
  );
}

function CredentialsCard({ runId, data }: { runId: string; data: RunEmail }) {
  const { user } = useMe();
  const m = useEmailMutations(runId);
  const [password, setPassword] = useState<string | null>(null);
  const [loginEdit, setLoginEdit] = useState<string | null>(null);
  const [regen, setRegen] = useState(false);
  const c = data.credentials;
  const manage = can(user.role, 'email.manage') && !c?.emailedAt;

  if (!c) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Client credentials</CardTitle>
          <CardDescription>Created when the final review is marked done.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Client credentials</CardTitle>
        <CardDescription>
          The client’s Canvas-AI login. The password is removed once the email is sent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid grid-cols-[7rem_1fr] items-center gap-y-3">
          <span className="text-muted-foreground">Login</span>
          {loginEdit !== null ? (
            <span className="flex gap-2">
              <Input
                value={loginEdit}
                onChange={(ev) => setLoginEdit(ev.target.value)}
                type="email"
                aria-label="Login email"
              />
              <Button
                size="sm"
                onClick={() =>
                  m.login.mutate(loginEdit, {
                    onSuccess: () => setLoginEdit(null),
                    onError: (err) => toastError(err),
                  })
                }
              >
                Save
              </Button>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {c.loginId || <span className="text-warning">No email - set one</span>}
              {manage && (
                <Button
                  variant="link"
                  className="h-auto p-0"
                  onClick={() => setLoginEdit(c.loginId)}
                >
                  Edit
                </Button>
              )}
            </span>
          )}

          <span className="text-muted-foreground">Password</span>
          {c.passwordAvailable ? (
            <span className="flex flex-wrap items-center gap-2">
              <code className="rounded bg-muted px-2 py-1 font-mono">
                {password ?? '••••••••••••••••'}
              </code>
              {can(user.role, 'credentials.reveal') && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={password ? 'Hide password' : 'Reveal password'}
                  onClick={() =>
                    password
                      ? setPassword(null)
                      : m.reveal.mutate(undefined, {
                          onSuccess: setPassword,
                          onError: (err) => toastError(err),
                        })
                  }
                >
                  {password ? <EyeOff /> : <Eye />}
                </Button>
              )}
              {password && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Copy password"
                  onClick={() => {
                    void navigator.clipboard?.writeText(password);
                    m.copied.mutate();
                    toastSuccess('Copied');
                  }}
                >
                  <Copy />
                </Button>
              )}
              {manage && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Regenerate password"
                  onClick={() => setRegen(true)}
                >
                  <RefreshCw />
                </Button>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">
              Removed after sending ({formatDateTime(c.purgedAt)})
            </span>
          )}

          <span className="text-muted-foreground">Canvas account</span>
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge
              tone={
                c.canvasAccountStatus === 'created'
                  ? 'success'
                  : c.canvasAccountStatus === 'failed'
                    ? 'danger'
                    : 'warning'
              }
            >
              {c.canvasAccountStatus === 'created'
                ? 'Created'
                : c.canvasAccountStatus === 'failed'
                  ? 'Failed'
                  : 'Pending'}
            </StatusBadge>
            {manage && c.canvasAccountStatus !== 'created' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  m.canvas.mutate(
                    { status: 'created' },
                    {
                      onSuccess: () => toastSuccess('Canvas account marked created'),
                      onError: (err) => toastError(err),
                    },
                  )
                }
              >
                Mark created
              </Button>
            )}
          </span>
        </div>
        {c.canvasAccountStatus !== 'created' && (
          <p className="text-xs text-muted-foreground">
            Canvas-AI has no account API yet (pending P2): create the account there with this login
            and password, then mark it created. The email waits until you do.
          </p>
        )}
      </CardContent>
      <ConfirmDialog
        open={regen}
        onOpenChange={setRegen}
        title="Regenerate the password?"
        description="The current password stops being the one that is emailed. If you already created the Canvas account with it, update it there too."
        confirmLabel="Regenerate"
        pending={m.regenerate.isPending}
        onConfirm={() =>
          m.regenerate.mutate(undefined, {
            onSuccess: () => {
              setPassword(null);
              setRegen(false);
              toastSuccess('New password generated');
            },
            onError: (err) => toastError(err),
          })
        }
      />
    </Card>
  );
}

function MessageCard({ runId, data }: { runId: string; data: RunEmail }) {
  const { user } = useMe();
  const m = useEmailMutations(runId);
  const msg = data.message;
  const [when, setWhen] = useState(msg?.sendAfter ? toLocalInput(msg.sendAfter) : '');
  const [confirmSend, setConfirmSend] = useState(false);
  const manage =
    can(user.role, 'email.manage') && msg && msg.status !== 'sent' && msg.status !== 'sending';
  const open = data.gates.filter((g) => !g.ok);
  const sendReason = !msg
    ? 'The email is created when the final review is done'
    : msg.status === 'blocked'
      ? 'Unblock the email first'
      : open
          // Send now passes a schedule and a halt; everything else must be met.
          .filter((g) => g.key !== 'schedule' && g.key !== 'halt')
          .map((g) => g.label)
          .join('; ') || null;
  const heldAndReady = data.policy.halted && msg?.status === 'scheduled' && sendReason === null;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base">Credentials email</CardTitle>
          <CardDescription>
            {msg?.sentAt
              ? `Sent ${formatDateTime(msg.sentAt)} to ${msg.toEmail}`
              : data.policy.halted
                ? 'Halted: it is sent only when someone presses Send now.'
                : 'Sent automatically once every condition below is met.'}
          </CardDescription>
        </div>
        <EmailStatusBadge status={msg?.status ?? null} />
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <ul className="space-y-1.5 text-sm" aria-label="Conditions">
            {data.gates.map((g) => (
              <li key={g.key} className="flex items-start gap-2">
                {g.ok ? (
                  <Check className="mt-0.5 size-4 text-success" />
                ) : (
                  <CircleDashed className="mt-0.5 size-4 text-muted-foreground" />
                )}
                <span className={g.ok ? '' : 'text-muted-foreground'}>{g.label}</span>
              </li>
            ))}
          </ul>
          {msg?.lastError && (
            <p className="rounded-md bg-danger-soft p-2 text-sm text-danger">
              Attempt {msg.attempts} failed: {msg.lastError}
            </p>
          )}
          {manage && heldAndReady && (
            <Alert>
              <AlertDescription>
                Everything is ready. The email is halted until you press Send now.
              </AlertDescription>
            </Alert>
          )}
          {manage && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1">
                  <Label htmlFor="send-after">Not before</Label>
                  <Input
                    id="send-after"
                    type="datetime-local"
                    value={when}
                    onChange={(ev) => setWhen(ev.target.value)}
                    className="w-56"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() =>
                    m.schedule.mutate(
                      { id: msg.id, sendAfter: when ? new Date(when).toISOString() : null },
                      {
                        onSuccess: () => toastSuccess(when ? 'Scheduled' : 'Schedule cleared'),
                        onError: (err) => toastError(err),
                      },
                    )
                  }
                >
                  {when ? 'Schedule' : 'Clear schedule'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {msg.status === 'blocked' ? (
                  <Button
                    variant="outline"
                    onClick={() => m.unblock.mutate(msg.id, { onError: (err) => toastError(err) })}
                  >
                    Unblock
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => m.block.mutate(msg.id, { onError: (err) => toastError(err) })}
                  >
                    Block this email
                  </Button>
                )}
                <DisabledReason reason={sendReason}>
                  <Button disabled={Boolean(sendReason)} onClick={() => setConfirmSend(true)}>
                    <Send /> {msg.status === 'failed' ? 'Retry now' : 'Send now'}
                  </Button>
                </DisabledReason>
              </div>
            </div>
          )}
        </div>
        {data.preview && (
          <div className="rounded-lg border">
            <p className="border-b px-3 py-2 text-xs text-muted-foreground">
              To <span className="text-foreground">{data.preview.to || '-'}</span> ·{' '}
              {data.preview.subject}
            </p>
            <pre className="p-3 font-sans text-sm whitespace-pre-wrap">{data.preview.text}</pre>
          </div>
        )}
      </CardContent>
      {msg && (
        <ConfirmDialog
          open={confirmSend}
          onOpenChange={setConfirmSend}
          title="Send the credentials email now?"
          description={`It goes to ${data.credentials?.loginId} immediately and cannot be recalled. The password is removed from the panel afterwards.`}
          confirmLabel="Send now"
          pending={m.sendNow.isPending}
          onConfirm={() =>
            m.sendNow.mutate(msg.id, {
              onSuccess: () => {
                setConfirmSend(false);
                toastSuccess('Email sent');
              },
              onError: (err) => {
                setConfirmSend(false);
                toastError(err);
              },
            })
          }
        />
      )}
    </Card>
  );
}

/** ISO instant → value for <input type="datetime-local"> in the viewer's zone. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
